import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getEventById } from "../api/events";
import "./GroupCeremonyPagesPage.css";

const getStorageKey = (eventId) => `event-grouping:${eventId}`;

function buildPages(event) {
  if (!event) {
    return [];
  }

  const pages = [
    {
      id: event.page_id || `event-${event.id}`,
      entityId: event.id,
      entityType: "event",
      pageIndex: event.page_index ?? 0,
      imageUrl: event.image_url,
      title: event.title || "Cover Page",
    },
    ...((event.subevents || []).map((subevent, index) => ({
      id: subevent.page_id || `subevent-${subevent.id || index}`,
      entityId: subevent.id,
      entityType: "subevent",
      pageIndex: subevent.page_index ?? index + 1,
      imageUrl: subevent.image_url,
      title: subevent.title || `Page ${index + 2}`,
    }))),
  ];

  return pages.sort((a, b) => a.pageIndex - b.pageIndex);
}

function buildDefaultGrouping(pages) {
  return pages.map((page, index) => ({
    pageId: page.id,
    pageIndex: page.pageIndex,
    mode: index === 0 ? "new" : "new",
  }));
}

function normalizeGrouping(pages, savedGrouping) {
  const defaultGrouping = buildDefaultGrouping(pages);
  if (!savedGrouping || !Array.isArray(savedGrouping)) {
    return defaultGrouping;
  }

  const savedMap = new Map(savedGrouping.map((item) => [item.pageId, item]));

  return defaultGrouping.map((item, index) => {
    if (index === 0) {
      return { ...item, mode: "new" };
    }

    const saved = savedMap.get(item.pageId);
    return {
      ...item,
      mode: saved?.mode === "same" ? "same" : "new",
    };
  });
}

function groupPages(pages, grouping) {
  const groupingMap = new Map(grouping.map((item) => [item.pageId, item.mode]));

  return pages.reduce((ceremonies, page, index) => {
    const mode = index === 0 ? "new" : groupingMap.get(page.id) || "new";

    if (mode === "same" && ceremonies.length > 0) {
      ceremonies[ceremonies.length - 1].pages.push(page);
      return ceremonies;
    }

    ceremonies.push({
      id: `ceremony-${ceremonies.length + 1}`,
      title: `Ceremony ${ceremonies.length + 1}`,
      pages: [page],
    });

    return ceremonies;
  }, []);
}

export function GroupCeremonyPagesPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [grouping, setGrouping] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) {
        setIsLoading(false);
        return;
      }

      try {
        const eventData = await getEventById(eventId);
        setEvent(eventData);

        const pages = buildPages(eventData);
        const savedValue = window.sessionStorage.getItem(getStorageKey(eventId));
        const parsedSavedValue = savedValue ? JSON.parse(savedValue) : null;
        setGrouping(normalizeGrouping(pages, parsedSavedValue));
      } catch (error) {
        console.error("Failed to load event for grouping:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  const pages = useMemo(() => buildPages(event), [event]);
  const ceremonyGroups = useMemo(() => groupPages(pages, grouping), [pages, grouping]);

  useEffect(() => {
    if (!eventId || grouping.length === 0) {
      return;
    }

    window.sessionStorage.setItem(getStorageKey(eventId), JSON.stringify(grouping));
  }, [eventId, grouping]);

  const handleGroupingChange = (pageId, mode) => {
    setGrouping((current) =>
      current.map((item, index) => {
        if (item.pageId !== pageId) {
          return item;
        }
        return { ...item, mode: index === 0 ? "new" : mode };
      })
    );
  };

  if (isLoading) {
    return (
      <div className="group-pages-loading">
        <div className="group-pages-loading-card">
          <p className="group-pages-kicker">Step 1 of 2</p>
          <h1>Loading ceremony pages...</h1>
        </div>
      </div>
    );
  }

  if (!event || pages.length === 0) {
    return (
      <div className="group-pages-loading">
        <div className="group-pages-loading-card">
          <p className="group-pages-kicker">Step 1 of 2</p>
          <h1>No ceremony pages found</h1>
          <button
            type="button"
            className="group-pages-secondary-btn"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group-pages-page">
      <div className="group-pages-shell">
        <header className="group-pages-header">
          <div>
            <p className="group-pages-kicker">Step 1 of 2</p>
            <h1>Organize Ceremonies</h1>
            <p className="group-pages-subcopy">
              Review each invitation page and tell us whether it starts a new ceremony or
              continues the previous one.
            </p>
          </div>

          <div className="group-pages-actions">
            <button
              type="button"
              className="group-pages-secondary-btn"
              onClick={() => navigate(-1)}
            >
              Back to Preview
            </button>
            <button
              type="button"
              className="group-pages-primary-btn"
              onClick={() =>
                navigate("/event-manager", {
                  state: {
                    eventId,
                    pageGrouping: grouping,
                    ceremonyGroups,
                  },
                })
              }
            >
              Continue to Ceremony Details
            </button>
          </div>
        </header>

        <section className="group-pages-summary-grid">
          <div className="group-pages-summary-card">
            <span>Total Pages</span>
            <strong>{pages.length}</strong>
          </div>
          <div className="group-pages-summary-card">
            <span>Ceremonies</span>
            <strong>{ceremonyGroups.length}</strong>
          </div>
          <div className="group-pages-summary-card">
            <span>Invitation</span>
            <strong>{event.title || "Event"}</strong>
          </div>
        </section>

        <div className="group-pages-content">
          <section className="group-pages-list">
            {pages.map((page, index) => {
              const currentMode = grouping.find((item) => item.pageId === page.id)?.mode || "new";
              const ceremonyNumber =
                ceremonyGroups.findIndex((group) => group.pages.some((groupPage) => groupPage.id === page.id)) + 1;

              return (
                <article key={page.id} className="group-page-card">
                  <div className="group-page-preview">
                    {page.imageUrl ? (
                      <img src={page.imageUrl} alt={`Page ${page.pageIndex + 1}`} />
                    ) : (
                      <div className="group-page-placeholder">No Preview</div>
                    )}
                  </div>

                  <div className="group-page-details">
                    <div className="group-page-meta">
                      <p className="group-page-index">Page {page.pageIndex + 1}</p>
                      <h2>{page.title || `Page ${page.pageIndex + 1}`}</h2>
                      <p className="group-page-assignment">Currently in Ceremony {ceremonyNumber}</p>
                    </div>

                    <div className="grouping-toggle-row">
                      <button
                        type="button"
                        className={`grouping-toggle-btn ${currentMode === "new" ? "active" : ""}`}
                        onClick={() => handleGroupingChange(page.id, "new")}
                      >
                        Starts new ceremony
                      </button>
                      <button
                        type="button"
                        className={`grouping-toggle-btn ${currentMode === "same" ? "active" : ""}`}
                        onClick={() => handleGroupingChange(page.id, "same")}
                        disabled={index === 0}
                      >
                        Part of previous ceremony
                      </button>
                    </div>

                    {index === 0 ? (
                      <p className="group-page-note">
                        The first page always starts the first ceremony group.
                      </p>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </section>

          <aside className="group-pages-sidebar">
            <div className="group-pages-sidebar-card">
              <p className="group-pages-sidebar-kicker">Live grouping</p>
              <h2>Ceremony Preview</h2>

              <div className="group-pages-sidebar-groups">
                {ceremonyGroups.map((group, index) => (
                  <div key={group.id} className="group-pages-sidebar-group">
                    <div className="group-pages-sidebar-group-header">
                      <strong>Ceremony {index + 1}</strong>
                      <span>
                        {group.pages.length} page{group.pages.length === 1 ? "" : "s"}
                      </span>
                    </div>

                    <div className="group-pages-sidebar-thumbs">
                      {group.pages.map((page) => (
                        <div key={page.id} className="group-pages-sidebar-thumb">
                          {page.imageUrl ? (
                            <img src={page.imageUrl} alt={`Page ${page.pageIndex + 1}`} />
                          ) : (
                            <div className="group-page-placeholder compact">No Preview</div>
                          )}
                          <span>Page {page.pageIndex + 1}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
